// src/components/Search.jsx
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import secureLocalStorage from "react-secure-storage";
import {
  useNavigate,
  Link as RouterLink,
  NavLink,
  useSearchParams,
} from "react-router-dom";
import {
  Container,
  Grid,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Box,
  Button,
  Typography,
  Stack,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  CircularProgress,
} from "../ui";
import { useMediaQuery } from "@mui/material";
import useEntitySearch from "../hooks/useEntitySearch";
import useQueryParamSync from "../hooks/useQueryParamSync";
import { salespersonList } from "../lists/salespersons";
import "../styles/HomePage.css";

/* ------------------------------------------------------------------ */
/* Local storage helpers (legacy behaviors preserved)                 */
/* ------------------------------------------------------------------ */
const clearWarehouseLocal = () =>
  secureLocalStorage.removeItem("warehouseform");
const clearFormLocal = () => secureLocalStorage.removeItem("form");

/* ------------------------------------------------------------------ */
/* Column configuration                                               */
/* ------------------------------------------------------------------ */
/* Salespeople available for Quote filtering (shared canonical list)  */
/* ------------------------------------------------------------------ */
const QUOTE_SALESPEOPLE = salespersonList;

/* ------------------------------------------------------------------ */
/**
 * Default column sets keyed by SearchName.
 * NOTE: keep headers short; wrap with &nbsp; for non-breaking if needed.
 */
const COLUMN_MAP = {
  SerialNumber: [
    { id: "MachineID", header: "Machine ID", getValue: (r) => r.MachineID },
    { id: "ModelID", header: "Model ID", getValue: (r) => r.ModelID },
    {
      id: "SerialNumber",
      header: "Serial #",
      getValue: (r) => r.SerialNumber ?? r.Serial_No ?? "",
    },
    {
      id: "TDControlNumber",
      header: "Job Ticket",
      getValue: (r) => r.TDControlNumber,
    },
  ],

  "Job Ticket ID": [
    {
      id: "TDControlNumber",
      header: "Job Ticket",
      getValue: (r) => r.TDControlNumber,
    },
    {
      id: "JobCreateDate",
      header: "Created",
      getValue: (r) => r.JobCreateDate,
    },
    {
      id: "BillingCompanyName",
      header: "Company",
      getValue: (r) => r.BillingCompanyName,
    },
    {
      id: "JobName",
      header: "Name",
      getValue: (r) => r.JobName || r.SerialNumber || "",
    },
    {
      id: "Status",
      header: "Status",
      getValue: (r) => {
        const status = (r.Status || "").toString().toLowerCase();
        if (status === "deleted") return "Deleted";
        if (status === "cancelled") return "Cancelled";
        return r.Status || "Active";
      },
    },
  ],

  "Inquiry ID": [
    {
      id: "TDControlNumber",
      header: "Job Ticket",
      getValue: (r) => r.TDControlNumber,
    },
    {
      id: "JobCreateDate",
      header: "Created",
      getValue: (r) => r.JobCreateDate,
    },
    {
      id: "BillingCompanyName",
      header: "Company",
      getValue: (r) => r.BillingCompanyName,
    },
    {
      id: "JobName",
      header: "Name",
      getValue: (r) => r.JobName || r.SerialNumber || "",
    },
    {
      id: "Salesperson",
      header: "Salesperson",
      getValue: (r, { personnel }) => {
        const emp = personnel.find(
          (e) => String(e.EmployeeID) === String(r.EmployeeID),
        );
        return emp ? `${emp.FirstName} ${emp.LastName}` : "";
      },
    },
    {
      id: "Status",
      header: "Status",
      getValue: (r) => {
        const status = (r.Status || "").toString().toLowerCase();
        if (status === "deleted") return "Deleted";
        if (status === "cancelled") return "Cancelled";
        return r.Status || "Active";
      },
    },
  ],

  PurchaseOrder: [
    { id: "PO", header: "PO", getValue: (r) => r.PO },
    { id: "PODate", header: "PO Date", getValue: (r) => r.PODate },
    { id: "Requisitioner", header: "Req.", getValue: (r) => r.Requisitioner },
    {
      id: "EstimatedPrice",
      header: "Est. Price",
      getValue: (r) =>
        `$${Number(r.EstimatedPrice || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      align: "right",
    },
    { id: "VendorName", header: "Vendor", getValue: (r) => r.VendorName },
    { id: "JobsiteNo", header: "Jobsite #", getValue: (r) => r.JobsiteNo },
    {
      id: "POCreateDate",
      header: "Created",
      getValue: (r) => r.POCreateDate,
    },
  ],

  Company: [
    { id: "CustomerID", header: "Customer ID", getValue: (r) => r.CustomerID },
    {
      id: "CompanyName",
      header: "Company Name",
      getValue: (r) => r.CompanyName,
    },
    {
      id: "CompanyPhone",
      header: "Phone",
      nowrap: true,
      getValue: (r) => r.CompanyPhone,
    },
  ],

  Carrier: [
    { id: "CarrierID", header: "Carrier ID", getValue: (r) => r.CarrierID },
    {
      id: "CarrierName",
      header: "Carrier Name",
      getValue: (r) => r.CarrierName,
    },
    {
      id: "PhoneNumber",
      header: "Phone",
      nowrap: true,
      getValue: (r) => r["Phone Number"] ?? r.CarrierPhone ?? "",
    },
    {
      id: "OperatingArea",
      header: "Operating Area",
      getValue: (r) => r["Operating Area"] ?? r.CarrierArea ?? "",
    },
  ],

  Vendor: [
    {
      id: "ID",
      header: "Vendor ID",
      getValue: (r) => r.ID ?? r.VendorID ?? "",
    },
    {
      id: "VendorName",
      header: "Vendor Name",
      getValue: (r) => r["Vendor Name"] ?? r.VendorName ?? "",
    },
    {
      id: "City",
      header: "City",
      getValue: (r) => r.City ?? "",
    },
    {
      id: "State",
      header: "State",
      getValue: (r) => r.State ?? "",
    },
    {
      id: "ContactName",
      header: "Contact",
      getValue: (r) => r["Contact Name"] ?? r.ContactName ?? "",
    },
    {
      id: "Phone",
      header: "Phone",
      nowrap: true,
      getValue: (r) => r["Phone Number"] ?? r.Phone ?? "",
    },
  ],

  Quote: [
    {
      id: "BidID",
      header: "Quote ID",
      getValue: (r) => r.BidID,
      align: "center",
    },
    {
      id: "BidDate",
      header: "Quote Date",
      getValue: (r) => r.BidDate,
      align: "center",
    },
    {
      id: "BidCustomerName",
      header: "Company",
      getValue: (r) => r.BidCustomerName,
      align: "center",
    },
    {
      id: "JobName",
      header: "Job Name",
      getValue: (r) => r.JobName,
      align: "center",
    },
    {
      id: "Bid",
      header: "Quote",
      getValue: (r) => `$${r.Bid}`,
      align: "right",
    },
    {
      id: "Employee",
      header: "Employee",
      getValue: (r, { personnel }) => {
        const emp = personnel.find(
          (e) => String(e.EmployeeID) === String(r.SalesmanID),
        );
        return emp ? `${emp.FirstName} ${emp.LastName}` : "";
      },
      align: "center",
    },
    {
      id: "BidCreateDate",
      header: "Created",
      getValue: (r) => r.BidCreateDate,
      align: "center",
    },
  ],
};

/** Fallback generic job/inquiry columns */
const GENERIC_JOB_COLUMNS = [
  {
    id: "TDControlNumber",
    header: "Job Ticket",
    getValue: (r) => r.TDControlNumber,
  },
  {
    id: "BillingCompanyName",
    header: "Company",
    getValue: (r) => r.BillingCompanyName,
  },
  {
    id: "JobName",
    header: "Name",
    getValue: (r) => r.JobName || r.SerialNumber || "",
  },
];

/* ------------------------------------------------------------------ */
/* Results Table                                                      */
/* ------------------------------------------------------------------ */
function ResultsTable({
  rows,
  columns,
  onRowClick,
  stickyHeader = false,
  size = "small",
  isLoading = false,
  error = null,
  isTruncated = false,
  truncationLimit,
  hasFetched = false,
  emptyMessage = "No matches found",
}) {
  const colSpan = columns.length || 1;
  return (
    <>
      {/* Refine-in-place: keep prior rows visible with a thin progress bar. */}
      {isLoading && rows.length > 0 && <LinearProgress />}
      <TableContainer
        component={Paper}
        sx={{ maxHeight: stickyHeader ? 420 : "none" }}
      >
        <Table size={size} stickyHeader={stickyHeader}>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.align || "left"}
                  sx={{
                    ...(col.width ? { width: col.width } : {}),
                    ...(col.nowrap ? { whiteSpace: "nowrap" } : {}),
                  }}
                >
                  {col.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 3 }}>
                  <CircularProgress
                    size={20}
                    sx={{ mr: 1, verticalAlign: "middle" }}
                  />
                  Loading…
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 3 }}>
                  Search failed. Please try again.
                </TableCell>
              </TableRow>
            ) : rows.length === 0 && hasFetched ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 3 }}>
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={
                    row.id ??
                    row.POID ??
                    row.BidID ??
                    row.JobTicketID ??
                    row.TDControlNumber ??
                    row.CustomerID ??
                    row.CarrierID ??
                    JSON.stringify(row)
                  }
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => onRowClick(row)}
                >
                  {columns.map((col) => (
                    <TableCell
                      key={col.id}
                      align={col.align || "left"}
                      sx={col.nowrap ? { whiteSpace: "nowrap" } : undefined}
                    >
                      {col.getValue(row, { rows, columns })}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {isTruncated && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 1 }}
        >
          Showing the first {truncationLimit} results — refine your search to
          narrow them.
        </Typography>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Loading / error / truncation status line for the hand-rolled tables */
/* ------------------------------------------------------------------ */
function SearchStatus({ isLoading, error, isTruncated, truncationLimit }) {
  if (!isLoading && !error && !isTruncated) return null;
  return (
    <Box sx={{ mb: 1 }}>
      {isLoading && <LinearProgress />}
      {error && (
        <Alert severity="error" sx={{ mt: isLoading ? 1 : 0 }}>
          Search failed. Please try again.
        </Alert>
      )}
      {!isLoading && !error && isTruncated && (
        <Typography variant="caption" color="text.secondary">
          Showing the first {truncationLimit} results — refine your search to
          narrow them.
        </Typography>
      )}
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* Shared helper: instructions text                                   */
/* ------------------------------------------------------------------ */
function Instructions({ children }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1">{children}</Typography>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* Shared helper: search controls row                                 */
/* ------------------------------------------------------------------ */
function SearchControls({
  label = "Search",
  value,
  onChange,
  onKeyDown,
  children,
  widthProp = 12,
  autoFocus = false,
}) {
  return (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={12} sm={widthProp}>
        <TextField
          fullWidth
          label={label}
          variant="outlined"
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          autoFocus={autoFocus}
          sx={{ backgroundColor: "#fff" }}
        />
      </Grid>
      <Grid
        item
        xs={12}
        sm={12 - widthProp}
        sx={{
          display: "flex",
          gap: 1,
          justifyContent: { xs: "flex-start", sm: "flex-end" },
        }}
      >
        {children}
      </Grid>
    </Grid>
  );
}

const normalizeVendorRow = (r) => ({
  VendorName: r["Vendor Name"] ?? "",
  Address: r.Address ?? "",
  City: r.City ?? "",
  State: r.State ?? "",
  Zip: r["Zip Code"] ?? "",
  ContactName: r["Contact Name"] ?? "",
  Phone: r["Phone Number"] ?? "", // primary
  TollFreeNumber: r["800 Number"] ?? "", // optional alt
  VendorID: r.ID ?? "", // ✅ your backend sends `ID`
});

/* ================================================================== */
/* Main Component                                                     */
/* ================================================================== */
export default function Search(props) {
  const {
    SearchName,
    ButtonName,
    Inquiry = 0,
    Limit = 250,
    RowButton = "Select",
    HomePage = false,
    Small = false,
    Vendor = false,
    createCompany,
    cancelCreate,
    createThirdParty,
    WarehouseInventoryMode = false,
    debounceSearch = false,
    syncUrl = false,
  } = props;

  const navigate = useNavigate();
  const searchRef = useRef(null);
  // Phone-width flag: compacts the list tables and pagination controls
  const isPhone = useMediaQuery("(max-width:599.95px)");

  /* ------------------------------------------------------------------ */
  /* Search engine: one debounced, cancelable request path (issue #482) */
  /* ------------------------------------------------------------------ */
  const isGeneral = useMemo(
    () =>
      new Set([
        "Vendor",
        "SerialNumber",
        "PurchaseOrder",
        "Quote",
        "Company",
        "Document",
        "Carrier",
      ]).has(SearchName),
    [SearchName],
  );

  const buildQueryString = useCallback(
    (term) => {
      const enc = encodeURIComponent(term ?? "");
      return isGeneral
        ? `${SearchName}=true&SearchTerm=${enc}&Limit=${Limit}`
        : `Inquiry=${Inquiry}&SearchTerm=${enc}&Limit=${Limit}`;
    },
    [isGeneral, SearchName, Inquiry, Limit],
  );

  const parseResponse = useCallback(
    (data) => {
      if (!Array.isArray(data)) return { rows: [], personnel: [] };
      // [rows, personnel] tuple shape (job/inquiry/quote searches)
      if (data.length >= 2 && Array.isArray(data[1])) {
        let rows = data[0] || [];
        // Exclude rows without an Inquiry field (machine-only) or with a
        // mismatched Inquiry flag, for job/inquiry searches.
        if (!isGeneral && rows.length > 0) {
          rows = rows.filter(
            (r) => r.Inquiry != null && Number(r.Inquiry) === Inquiry,
          );
        }
        return { rows, personnel: data[1] || [] };
      }
      // flat array shape (vendors, carriers, companies, POs, serials)
      return { rows: data, personnel: [] };
    },
    [isGeneral, Inquiry],
  );

  // Seed the query from the URL (?q=) on full-page lists; PO keeps its "MH" seed.
  const [urlSearchParams] = useSearchParams();
  const seededQuery = syncUrl ? urlSearchParams.get("q") || "" : "";
  const initialQuery =
    seededQuery || (SearchName === "PurchaseOrder" ? "MH" : "");

  // Which modes auto-load a first page on mount (mirrors legacy initial fetches).
  const autoLoad =
    SearchName === "Carrier" ||
    (SearchName === "Vendor" && !Vendor) ||
    SearchName === "PurchaseOrder" ||
    SearchName === "Quote" ||
    ((SearchName === "Job Ticket ID" || SearchName === "Inquiry ID") &&
      RowButton !== "Select" &&
      !HomePage);

  // Embedded selectors (Small popover / PO vendor picker / debounced pickers)
  // clear their results on an empty box instead of listing everything.
  const clearOnEmpty = Small || Vendor || debounceSearch;

  const {
    query,
    setQuery,
    searchNow,
    results,
    personnel,
    isLoading,
    error,
    isTruncated,
    hasFetched,
  } = useEntitySearch({
    buildQueryString,
    parseResponse,
    limit: Limit,
    initialQuery,
    autoLoad,
    clearOnEmpty,
  });

  // Persist the query to the URL on full-page lists (shareable / restorable).
  useQueryParamSync(query, { enabled: syncUrl });

  // Aliases so the existing render/memo code keeps reading familiar names.
  const searchTerm = query;
  const notes = results;

  /* state */
  const [showResults, setShowResults] = useState(false);
  const [isCompanyCreate, setIsCompanyCreate] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [jobPage, setJobPage] = useState(1);
  const [jobRowsPerPage, setJobRowsPerPage] = useState(100);
  const [quotePage, setQuotePage] = useState(1);
  const [quoteRowsPerPage, setQuoteRowsPerPage] = useState(100);
  const [quoteEmployeeFilter, setQuoteEmployeeFilter] = useState("");
  const [poPage, setPoPage] = useState(1);
  const [poRowsPerPage, setPoRowsPerPage] = useState(100);
  const [jobStatusFilter, setJobStatusFilter] = useState("active"); // "active", "deleted", "all"

  /* Reset every mode's pagination to page 1 whenever the result set changes. */
  useEffect(() => {
    setPage(1);
    setJobPage(1);
    setQuotePage(1);
    setPoPage(1);
  }, [results]);

  /* ----- close popover on outside click (Small only) ----- */
  useEffect(() => {
    if (!Small) return;
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [Small]);

  /* ----- input handlers (single debounced, cancelable path) ----- */
  const handleInputChange = (e) => {
    setShowResults(true);
    setQuery(e.target.value);
  };

  const handleSearchNow = () => {
    setShowResults(true);
    searchNow();
  };

  const handleInputKey = (e) => {
    if (e.key === "Enter") handleSearchNow();
  };

  const handleCompanyCreateToggle = () => {
    const next = !isCompanyCreate;
    setIsCompanyCreate(next);

    if (next) {
      if (typeof createCompany === "function") {
        createCompany();
      }
    } else {
      if (typeof cancelCreate === "function") {
        cancelCreate();
      }
    }
  };

  function clearQuoteLocal() {
    secureLocalStorage.removeItem("EditQuote_new");
    secureLocalStorage.removeItem("EditQuote");
  }

  /* ----- new quote ----- */
  const handleNewQuoteClick = () => {
    clearQuoteLocal();
    navigate("/CreateQuote");
  };

  /* ------------------------------------------------------------------ */
  /* Row click URL builders (one per mode)                              */
  /* ------------------------------------------------------------------ */
  const buildUrl = useCallback(
    (row) => {
      switch (SearchName) {
        case "SerialNumber":
          // Upstream Action historically received the MachineID only;
          // we now pass url=null and row; let parent do what it needs.
          return null;

        case "Job Ticket ID":
          // Two contexts existed: Select vs Edit. We unify to:
          // When RowButton==="Select": parent chooses; we pass TDControlNumber.
          // When editing: go to /Edit<ButtonName>/<BillingCustomerID>/<JobTicketID>
          if (RowButton === "Select") {
            return row.TDControlNumber; // a value, not a route; parent handles
          }
          {
            const inq = row.Inquiry != null ? Number(row.Inquiry) : null;
            const entity =
              inq === 1 ? "Inquiry" : inq === 0 ? "Job" : ButtonName;
            return `/Edit${entity}/${row.BillingCustomerID}/${row.JobTicketID}`;
          }

        case "PurchaseOrder":
          return `/EditPurchaseOrder/${row.PO}`;

        case "Company":
          // parent historically used event target; now we pass route + row
          return null; // parent decides; we supply row

        case "Carrier":
          return `/EditCarrier/${row.CarrierID}`;

        case "Quote":
          return `/EditQuote/${row.BidID}`;

        case "Vendor":
          return `/EditVendor/${row.ID ?? row.VendorID}`;

        default:
          // generic job/inquiry
          if (RowButton === "Edit") {
            const inq = row.Inquiry != null ? Number(row.Inquiry) : null;
            const entity =
              inq === 1 ? "Inquiry" : inq === 0 ? "Job" : ButtonName;
            return `/Edit${entity}/${row.BillingCustomerID}/${row.JobTicketID}`;
          }
          if (RowButton === "Select") {
            // parent decides; provide TDControlNumber
            return row.TDControlNumber;
          }
          return null;
      }
    },
    [SearchName, RowButton, ButtonName],
  );

  /* ------------------------------------------------------------------ */
  /* Unified row click handler                                          */
  /* ------------------------------------------------------------------ */
  const handleRowClick = (row) => {
    // legacy housekeeping
    if (
      SearchName === "Job Ticket ID" ||
      SearchName === "Inquiry ID" ||
      RowButton === "Edit"
    ) {
      clearFormLocal();
    }
    if (SearchName === "WarehouseInventory") {
      clearWarehouseLocal();
    }
    if (SearchName === "Quote") {
      // Clear ALL Quote-related localStorage BEFORE navigation
      clearQuoteLocal();
      // Also clear any other potential Quote keys
      const prefix = "EditQuote_";
      for (let i = secureLocalStorage.length - 1; i >= 0; i--) {
        const k = secureLocalStorage.key(i);
        if (k && k.startsWith(prefix)) {
          secureLocalStorage.removeItem(k);
        }
      }
      secureLocalStorage.removeItem("NewQuote");
      secureLocalStorage.removeItem("NewQuote_FieldsOpen");
    }

    if (Vendor) {
      const vendorPayload = normalizeVendorRow(row);
      if (typeof props.Action === "function") {
        props.Action(vendorPayload);
      }
      setShowResults(false);
      return;
    }

    const url = buildUrl(row);

    if (typeof props.Action === "function") {
      props.Action(url, row);
    } else if (url && typeof url === "string" && url.startsWith("/")) {
      navigate(url);
    }
    setShowResults(false);
  };

  /* ------------------------------------------------------------------ */
  /* Pagination for Carrier                                             */
  /* ------------------------------------------------------------------ */
  const paginatedNotes = useMemo(() => {
    if (SearchName !== "Carrier" && SearchName !== "Vendor") return notes;
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return notes.slice(startIndex, endIndex);
  }, [notes, page, rowsPerPage, SearchName]);

  const totalPages = Math.ceil(notes.length / rowsPerPage);

  /* ------------------------------------------------------------------ */
  /* Filtering and Pagination for Job Ticket ID                         */
  /* ------------------------------------------------------------------ */
  const filteredJobs = useMemo(() => {
    if (SearchName !== "Job Ticket ID" && SearchName !== "Inquiry ID")
      return notes;
    if (jobStatusFilter === "all") return notes;

    // Helper to get normalized status
    const getStatus = (job) => (job.Status || "").toString().toLowerCase();

    if (jobStatusFilter === "deleted") {
      return notes.filter((job) => getStatus(job) === "deleted");
    }
    if (jobStatusFilter === "cancelled") {
      return notes.filter((job) => getStatus(job) === "cancelled");
    }
    // Default: "active" - show all jobs except deleted AND cancelled
    return notes.filter((job) => {
      const status = getStatus(job);
      return status !== "deleted" && status !== "cancelled";
    });
  }, [notes, jobStatusFilter, SearchName]);

  const paginatedJobs = useMemo(() => {
    if (SearchName !== "Job Ticket ID" && SearchName !== "Inquiry ID")
      return notes;
    const startIndex = (jobPage - 1) * jobRowsPerPage;
    const endIndex = startIndex + jobRowsPerPage;
    return filteredJobs.slice(startIndex, endIndex);
  }, [filteredJobs, jobPage, jobRowsPerPage, SearchName, notes]);

  const totalJobPages = Math.ceil(filteredJobs.length / jobRowsPerPage);

  /* ------------------------------------------------------------------ */
  /* Filter for Small variant - always hide deleted/cancelled jobs       */
  /* ------------------------------------------------------------------ */
  const filteredNotesForSmall = useMemo(() => {
    if (!Array.isArray(notes) || notes.length === 0) return notes;
    // Only filter if items have JobTicketID (job data)
    if (notes[0].JobTicketID === undefined) return notes;
    return notes.filter((job) => {
      const status = (job.Status || "").toString().toLowerCase();
      return status !== "deleted" && status !== "cancelled";
    });
  }, [notes]);

  /* ------------------------------------------------------------------ */
  /* Pagination for Quote                                               */
  /* ------------------------------------------------------------------ */
  const filteredQuotes = useMemo(() => {
    if (SearchName !== "Quote") return notes;
    if (!quoteEmployeeFilter) return notes;
    return notes.filter(
      (q) => String(q.SalesmanID) === String(quoteEmployeeFilter),
    );
  }, [notes, quoteEmployeeFilter, SearchName]);

  const paginatedQuotes = useMemo(() => {
    if (SearchName !== "Quote") return filteredQuotes;
    const startIndex = (quotePage - 1) * quoteRowsPerPage;
    const endIndex = startIndex + quoteRowsPerPage;
    return filteredQuotes.slice(startIndex, endIndex);
  }, [filteredQuotes, quotePage, quoteRowsPerPage, SearchName]);

  const totalQuotePages = Math.ceil(filteredQuotes.length / quoteRowsPerPage);

  /* ------------------------------------------------------------------ */
  /* Pagination for PurchaseOrder                                       */
  /* ------------------------------------------------------------------ */
  const paginatedPOs = useMemo(() => {
    if (SearchName !== "PurchaseOrder") return notes;
    const startIndex = (poPage - 1) * poRowsPerPage;
    const endIndex = startIndex + poRowsPerPage;
    return notes.slice(startIndex, endIndex);
  }, [notes, poPage, poRowsPerPage, SearchName]);

  const totalPOPages = Math.ceil(notes.length / poRowsPerPage);

  /* ------------------------------------------------------------------ */
  /* Build columns for this render                                      */
  /* ------------------------------------------------------------------ */
  const columns = useMemo(() => {
    // Parent override takes precedence
    if (Array.isArray(props.columns) && props.columns.length > 0) {
      return props.columns;
    }

    // Mode-specific builtins
    const baseCols = COLUMN_MAP[SearchName];
    if (baseCols) {
      // some col getValue rely on personnel; wrap to inject
      return baseCols.map((c) => ({
        ...c,
        getValue: (row, ctx) => c.getValue(row, { personnel, ...ctx }),
      }));
    }

    // vendor "Select" mode (SearchName not guaranteed)
    if (Vendor) {
      return [
        {
          id: "VendorName",
          header: "Vendor Name",
          getValue: (r) => r["Vendor Name"],
        },
        { id: "Address", header: "Address", getValue: (r) => r.Address },
        { id: "City", header: "City", getValue: (r) => r.City },
        { id: "State", header: "State", getValue: (r) => r.State },
        { id: "Zip", header: "Zip", getValue: (r) => r["Zip Code"] },
        {
          id: "Contact",
          header: "Contact",
          getValue: (r) => r["Contact Name"],
        },
        {
          id: "Phone",
          header: "Phone",
          nowrap: true,
          getValue: (r) => r["Phone Number"],
        },
      ];
    }

    // fallback to generic job/inquiry
    return GENERIC_JOB_COLUMNS;
  }, [props.columns, SearchName, Vendor, personnel]);

  // Columns without Status for Small and WarehouseInventoryMode
  const columnsWithoutStatus = useMemo(
    () => columns.filter((col) => col.id !== "Status"),
    [columns],
  );

  /* ------------------------------------------------------------------ */
  /* SMALL VARIANT (inline / navbar / homepage)                         */
  /* ------------------------------------------------------------------ */
  if (Small) {
    // For Small variant we show a popover table; for heavy result sets you
    // could add virtualization later.
    return (
      <Box ref={searchRef} sx={{ width: "100%", position: "relative" }}>
        <TextField
          fullWidth
          size="small"
          id="search-small"
          placeholder={
            ButtonName
              ? `Search ${ButtonName.endsWith("y") ? ButtonName.slice(0, -1) + "ies" : ButtonName + "s"}`
              : "Search"
          }
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleInputKey}
          autoComplete="off"
        />

        {showResults && (
          <Box
            className="search-results"
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              zIndex: 10,
              mt: 1,
              maxHeight: 320,
              overflow: "auto",
              minWidth: { xs: 0, sm: 400 },
              maxWidth: "90vw",
              backgroundColor: "background.paper",
              boxShadow: 3,
              borderRadius: 1,
            }}
          >
            <ResultsTable
              rows={filteredNotesForSmall}
              columns={columnsWithoutStatus}
              onRowClick={handleRowClick}
              size="small"
              isLoading={isLoading}
              error={error}
              hasFetched={hasFetched}
            />
          </Box>
        )}
      </Box>
    );
  }

  /* ------------------------------------------------------------------ */
  /* FULL VARIANTS BELOW                                                */
  /* ------------------------------------------------------------------ */

  /* Purchase Order Page */
  if (SearchName === "PurchaseOrder") {
    return (
      <>
        {/* ── Search Controls ───────────────────────────── */}
        <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 3 }, mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Search using PO Number, Company Name, or Vendor Name then press
            Enter or click Search.
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ alignItems: { xs: "stretch", sm: "center" } }}
          >
            <TextField
              fullWidth
              label="Search POs"
              value={searchTerm}
              onChange={handleInputChange}
              onKeyDown={handleInputKey}
              variant="outlined"
            />
            <Stack direction="row" spacing={2} sx={{ flexShrink: 0 }}>
              <Button
                variant="contained"
                onClick={handleSearchNow}
                sx={{
                  minWidth: 120,
                  whiteSpace: "nowrap",
                  flex: { xs: 1, sm: "none" },
                }}
              >
                Search
              </Button>
              <Button
                component={RouterLink}
                to="/CreatePurchaseOrder"
                variant="outlined"
                sx={{
                  minWidth: 140,
                  whiteSpace: "nowrap",
                  flex: { xs: 1, sm: "none" },
                }}
              >
                Create PO
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* ── Results Table ───────────────────────────── */}
        <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 3 } }}>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "center" },
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                gap: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Showing{" "}
                {paginatedPOs.length > 0 ? (poPage - 1) * poRowsPerPage + 1 : 0}
                -{Math.min(poPage * poRowsPerPage, notes.length)} of{" "}
                {notes.length} purchase orders
              </Typography>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Per page</InputLabel>
                <Select
                  value={poRowsPerPage}
                  label="Per page"
                  onChange={(e) => {
                    setPoRowsPerPage(Number(e.target.value));
                    setPoPage(1);
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {totalPOPages > 1 && (
              <Stack spacing={2}>
                <Pagination
                  count={totalPOPages}
                  page={poPage}
                  onChange={(event, value) => setPoPage(value)}
                  color="primary"
                  size={isPhone ? "small" : "medium"}
                  siblingCount={isPhone ? 0 : 1}
                  showFirstButton={!isPhone}
                  showLastButton={!isPhone}
                />
              </Stack>
            )}
          </Box>

          <SearchStatus
            isLoading={isLoading}
            error={error}
            isTruncated={isTruncated}
            truncationLimit={Limit}
          />

          <TableContainer>
            <Table
              size="small"
              sx={{ border: "1px solid rgba(224, 224, 224, 1)" }}
            >
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                    }}
                  >
                    PO
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                      display: { xs: "none", sm: "table-cell" },
                    }}
                  >
                    PO Date
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                    }}
                  >
                    Requisitioner
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                    }}
                  >
                    Est. Price
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                    }}
                  >
                    Vendor
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                      display: { xs: "none", sm: "table-cell" },
                    }}
                  >
                    Jobsite #
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                      display: { xs: "none", sm: "table-cell" },
                    }}
                  >
                    Created
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedPOs && paginatedPOs.length > 0 ? (
                  paginatedPOs.map((po, index) => {
                    // Build the navigation URL
                    const poUrl = buildUrl(po);

                    return (
                      <TableRow
                        key={po.PO || index}
                        component={NavLink}
                        to={poUrl}
                        sx={{
                          cursor: "pointer",
                          backgroundColor:
                            index % 2 === 0
                              ? "transparent"
                              : "rgba(0, 0, 0, 0.05)",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.08)",
                          },
                          textDecoration: "none",
                          color: "inherit",
                        }}
                      >
                        <TableCell
                          sx={{
                            border: "1px solid rgba(224, 224, 224, 1)",
                          }}
                        >
                          {po.PO}
                        </TableCell>
                        <TableCell
                          sx={{
                            border: "1px solid rgba(224, 224, 224, 1)",
                            display: { xs: "none", sm: "table-cell" },
                          }}
                        >
                          {po.PODate}
                        </TableCell>
                        <TableCell
                          sx={{
                            border: "1px solid rgba(224, 224, 224, 1)",
                          }}
                        >
                          {po.Requisitioner}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            border: "1px solid rgba(224, 224, 224, 1)",
                          }}
                        >
                          $
                          {Number(po.EstimatedPrice || 0).toLocaleString(
                            "en-US",
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            border: "1px solid rgba(224, 224, 224, 1)",
                          }}
                        >
                          {po.VendorName}
                        </TableCell>
                        <TableCell
                          sx={{
                            border: "1px solid rgba(224, 224, 224, 1)",
                            display: { xs: "none", sm: "table-cell" },
                          }}
                        >
                          {po.JobsiteNo}
                        </TableCell>
                        <TableCell
                          sx={{
                            border: "1px solid rgba(224, 224, 224, 1)",
                            display: { xs: "none", sm: "table-cell" },
                          }}
                        >
                          {po.POCreateDate}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      align="center"
                      sx={{
                        border: "1px solid rgba(224, 224, 224, 1)",
                        py: 3,
                      }}
                    >
                      {isLoading ? "Loading…" : "No purchase orders found"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPOPages > 1 && (
            <Box
              sx={{
                mt: 2,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Pagination
                count={totalPOPages}
                page={poPage}
                onChange={(event, value) => setPoPage(value)}
                color="primary"
                size={isPhone ? "small" : "medium"}
                siblingCount={isPhone ? 0 : 1}
                showFirstButton={!isPhone}
                showLastButton={!isPhone}
              />
            </Box>
          )}
        </Paper>
      </>
    );
  }

  /* Company Page */
  if (SearchName === "Company") {
    return (
      <>
        <SearchControls
          label="Search Companies"
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleInputKey}
          widthProp={9}
        >
          <Button variant="contained" onClick={handleSearchNow}>
            Search
          </Button>
          <Button
            variant="outlined"
            color={isCompanyCreate ? "error" : "secondary"}
            onClick={handleCompanyCreateToggle}
          >
            {isCompanyCreate ? "Cancel" : `Create ${ButtonName}`}
          </Button>
        </SearchControls>

        {showResults && (
          <Box sx={{ mt: 2 }}>
            <ResultsTable
              rows={notes}
              columns={columns}
              onRowClick={handleRowClick}
              stickyHeader
              isLoading={isLoading}
              error={error}
              isTruncated={isTruncated}
              truncationLimit={Limit}
              hasFetched={hasFetched}
              emptyMessage="No companies found"
            />
          </Box>
        )}
      </>
    );
  }

  /* Carrier Page */
  if (SearchName === "Carrier") {
    return (
      <>
        {/* ── Search Section ───────────────────────────── */}
        <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 3 }, mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Search using Carrier ID, Carrier Name, or Phone Number.
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
          >
            <TextField
              fullWidth
              label="Search"
              variant="outlined"
              value={searchTerm}
              onChange={handleInputChange}
              placeholder="Type to search..."
            />

            <Button
              component={RouterLink}
              to="/CreateCarrier"
              variant="contained"
              color="primary"
              sx={{
                minWidth: 140,
                whiteSpace: "nowrap",
              }}
            >
              Create Carrier
            </Button>
          </Stack>
        </Paper>

        {/* ── Results Table ───────────────────────────── */}
        <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 3 } }}>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Showing{" "}
                {paginatedNotes.length > 0 ? (page - 1) * rowsPerPage + 1 : 0}-
                {Math.min(page * rowsPerPage, notes.length)} of {notes.length}{" "}
                carriers
              </Typography>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Per page</InputLabel>
                <Select
                  value={rowsPerPage}
                  label="Per page"
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {totalPages > 1 && (
              <Stack spacing={2}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(event, value) => setPage(value)}
                  color="primary"
                  size={isPhone ? "small" : "medium"}
                  siblingCount={isPhone ? 0 : 1}
                  showFirstButton={!isPhone}
                  showLastButton={!isPhone}
                />
              </Stack>
            )}
          </Box>

          <SearchStatus
            isLoading={isLoading}
            error={error}
            isTruncated={isTruncated}
            truncationLimit={Limit}
          />

          <TableContainer>
            <Table
              size="small"
              sx={{ border: "1px solid rgba(224, 224, 224, 1)" }}
            >
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                    }}
                  >
                    Carrier ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                    }}
                  >
                    Carrier Name
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                    }}
                  >
                    Phone
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                    }}
                  >
                    Operating Area
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedNotes && paginatedNotes.length > 0 ? (
                  paginatedNotes.map((carrier, index) => (
                    <TableRow
                      key={carrier.CarrierID}
                      component={NavLink}
                      to={`/EditCarrier/${carrier.CarrierID}`}
                      sx={{
                        cursor: "pointer",
                        backgroundColor:
                          index % 2 === 0
                            ? "transparent"
                            : "rgba(0, 0, 0, 0.05)",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.08)",
                        },
                        textDecoration: "none",
                        color: "inherit",
                      }}
                    >
                      <TableCell
                        sx={{
                          border: "1px solid rgba(224, 224, 224, 1)",
                        }}
                      >
                        {carrier.CarrierID}
                      </TableCell>
                      <TableCell
                        sx={{
                          border: "1px solid rgba(224, 224, 224, 1)",
                        }}
                      >
                        {carrier.CarrierName}
                      </TableCell>
                      <TableCell
                        sx={{
                          border: "1px solid rgba(224, 224, 224, 1)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {carrier["Phone Number"] ?? carrier.CarrierPhone ?? ""}
                      </TableCell>
                      <TableCell
                        sx={{
                          border: "1px solid rgba(224, 224, 224, 1)",
                        }}
                      >
                        {carrier["Operating Area"] ?? carrier.CarrierArea ?? ""}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      align="center"
                      sx={{
                        border: "1px solid rgba(224, 224, 224, 1)",
                        py: 3,
                      }}
                    >
                      {isLoading ? "Loading…" : "No carriers found"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box
              sx={{
                mt: 2,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={(event, value) => setPage(value)}
                color="primary"
                size={isPhone ? "small" : "medium"}
                siblingCount={isPhone ? 0 : 1}
                showFirstButton={!isPhone}
                showLastButton={!isPhone}
              />
            </Box>
          )}
        </Paper>
      </>
    );
  }

  /* Vendor Page */
  if (SearchName === "Vendor" && !Vendor) {
    return (
      <>
        {/* ── Search Section ───────────────────────────── */}
        <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 3 }, mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Search using Vendor ID, Vendor Name, City, or Contact Name.
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
          >
            <TextField
              fullWidth
              label="Search"
              variant="outlined"
              value={searchTerm}
              onChange={handleInputChange}
              placeholder="Type to search..."
            />

            <Button
              component={RouterLink}
              to="/CreateVendor"
              variant="contained"
              color="primary"
              sx={{
                minWidth: 140,
                whiteSpace: "nowrap",
              }}
            >
              Create Vendor
            </Button>
          </Stack>
        </Paper>

        {/* ── Results Table ───────────────────────────── */}
        <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 3 } }}>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Showing{" "}
                {paginatedNotes.length > 0 ? (page - 1) * rowsPerPage + 1 : 0}-
                {Math.min(page * rowsPerPage, notes.length)} of {notes.length}{" "}
                vendors
              </Typography>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Per page</InputLabel>
                <Select
                  value={rowsPerPage}
                  label="Per page"
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {totalPages > 1 && (
              <Stack spacing={2}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(event, value) => setPage(value)}
                  color="primary"
                  size={isPhone ? "small" : "medium"}
                  siblingCount={isPhone ? 0 : 1}
                  showFirstButton={!isPhone}
                  showLastButton={!isPhone}
                />
              </Stack>
            )}
          </Box>

          <SearchStatus
            isLoading={isLoading}
            error={error}
            isTruncated={isTruncated}
            truncationLimit={Limit}
          />

          <TableContainer>
            <Table
              size="small"
              sx={{ border: "1px solid rgba(224, 224, 224, 1)" }}
            >
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                    }}
                  >
                    Vendor ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                    }}
                  >
                    Vendor Name
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                    }}
                  >
                    City
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                    }}
                  >
                    State
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                    }}
                  >
                    Contact
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                    }}
                  >
                    Phone
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedNotes && paginatedNotes.length > 0 ? (
                  paginatedNotes.map((vendor, index) => (
                    <TableRow
                      key={vendor.ID ?? vendor.VendorID ?? index}
                      component={NavLink}
                      to={`/EditVendor/${vendor.ID ?? vendor.VendorID}`}
                      sx={{
                        cursor: "pointer",
                        backgroundColor:
                          index % 2 === 0
                            ? "transparent"
                            : "rgba(0, 0, 0, 0.05)",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.08)",
                        },
                        textDecoration: "none",
                        color: "inherit",
                      }}
                    >
                      <TableCell
                        sx={{
                          border: "1px solid rgba(224, 224, 224, 1)",
                        }}
                      >
                        {vendor.ID ?? vendor.VendorID ?? ""}
                      </TableCell>
                      <TableCell
                        sx={{
                          border: "1px solid rgba(224, 224, 224, 1)",
                        }}
                      >
                        {vendor["Vendor Name"] ?? vendor.VendorName ?? ""}
                      </TableCell>
                      <TableCell
                        sx={{
                          border: "1px solid rgba(224, 224, 224, 1)",
                        }}
                      >
                        {vendor.City ?? ""}
                      </TableCell>
                      <TableCell
                        sx={{
                          border: "1px solid rgba(224, 224, 224, 1)",
                        }}
                      >
                        {vendor.State ?? ""}
                      </TableCell>
                      <TableCell
                        sx={{
                          border: "1px solid rgba(224, 224, 224, 1)",
                        }}
                      >
                        {vendor["Contact Name"] ?? vendor.ContactName ?? ""}
                      </TableCell>
                      <TableCell
                        sx={{
                          border: "1px solid rgba(224, 224, 224, 1)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {vendor["Phone Number"] ?? vendor.Phone ?? ""}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      align="center"
                      sx={{
                        border: "1px solid rgba(224, 224, 224, 1)",
                        py: 3,
                      }}
                    >
                      {isLoading ? "Loading…" : "No vendors found"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box
              sx={{
                mt: 2,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={(event, value) => setPage(value)}
                color="primary"
                size={isPhone ? "small" : "medium"}
                siblingCount={isPhone ? 0 : 1}
                showFirstButton={!isPhone}
                showLastButton={!isPhone}
              />
            </Box>
          )}
        </Paper>
      </>
    );
  }

  if (SearchName === "Job Ticket ID" && WarehouseInventoryMode) {
    return (
      <Container disableGutters>
        <Instructions>
          Search using Job Ticket, Company Name, or Job Name then press Enter or
          click Search.
        </Instructions>

        <SearchControls
          label="Search Jobs"
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleInputKey}
          widthProp={9}
        >
          <Button variant="contained" onClick={handleSearchNow}>
            Search
          </Button>
        </SearchControls>

        {showResults && (
          <Box sx={{ mt: 2 }}>
            <ResultsTable
              rows={filteredJobs}
              columns={columnsWithoutStatus}
              onRowClick={handleRowClick}
              stickyHeader
              isLoading={isLoading}
              error={error}
              isTruncated={isTruncated}
              truncationLimit={Limit}
              hasFetched={hasFetched}
              emptyMessage="No jobs found"
            />
          </Box>
        )}
      </Container>
    );
  }

  /* Job Ticket ID / Inquiry ID Page */
  const isJobOrInquiryPage =
    SearchName === "Job Ticket ID" || SearchName === "Inquiry ID";
  const isInquiryPage = SearchName === "Inquiry ID";
  const entityLabel = isInquiryPage ? "Inquiry" : "Job";
  const entityLabelPlural = isInquiryPage ? "Inquiries" : "Jobs";

  if (isJobOrInquiryPage) {
    return (
      <>
        {/* ── Search Controls ───────────────────────────── */}
        <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 3 }, mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Search using Job Ticket, Company Name, or {entityLabel} Name then
            press Enter or click Search.
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ alignItems: { xs: "stretch", sm: "center" } }}
          >
            <TextField
              fullWidth
              label={`Search ${entityLabelPlural}`}
              value={searchTerm}
              onChange={handleInputChange}
              onKeyDown={handleInputKey}
              variant="outlined"
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={jobStatusFilter}
                label="Status"
                onChange={(e) => {
                  setJobStatusFilter(e.target.value);
                  setJobPage(1);
                }}
              >
                <MenuItem value="active">Active Only</MenuItem>
                <MenuItem value="deleted">Deleted Only</MenuItem>
                <MenuItem value="cancelled">Cancelled Only</MenuItem>
                <MenuItem value="all">All {entityLabelPlural}</MenuItem>
              </Select>
            </FormControl>
            <Stack direction="row" spacing={2} sx={{ flexShrink: 0 }}>
              <Button
                variant="contained"
                onClick={handleSearchNow}
                sx={{
                  minWidth: 120,
                  whiteSpace: "nowrap",
                  flex: { xs: 1, sm: "none" },
                }}
              >
                Search
              </Button>
              <Button
                component={RouterLink}
                to="/CompanySearch"
                variant="outlined"
                sx={{
                  minWidth: 140,
                  whiteSpace: "nowrap",
                  flex: { xs: 1, sm: "none" },
                }}
              >
                Create {entityLabel}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* ── Results Table ───────────────────────────── */}
        <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 3 } }}>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "center" },
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                gap: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Showing{" "}
                {paginatedJobs.length > 0
                  ? (jobPage - 1) * jobRowsPerPage + 1
                  : 0}
                -{Math.min(jobPage * jobRowsPerPage, filteredJobs.length)} of{" "}
                {filteredJobs.length} {entityLabelPlural.toLowerCase()}
              </Typography>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Per page</InputLabel>
                <Select
                  value={jobRowsPerPage}
                  label="Per page"
                  onChange={(e) => {
                    setJobRowsPerPage(Number(e.target.value));
                    setJobPage(1);
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {totalJobPages > 1 && (
              <Stack spacing={2}>
                <Pagination
                  count={totalJobPages}
                  page={jobPage}
                  onChange={(event, value) => setJobPage(value)}
                  color="primary"
                  size={isPhone ? "small" : "medium"}
                  siblingCount={isPhone ? 0 : 1}
                  showFirstButton={!isPhone}
                  showLastButton={!isPhone}
                />
              </Stack>
            )}
          </Box>

          <SearchStatus
            isLoading={isLoading}
            error={error}
            isTruncated={isTruncated}
            truncationLimit={Limit}
          />

          <TableContainer>
            <Table
              size="small"
              sx={{ border: "1px solid rgba(224, 224, 224, 1)" }}
            >
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                    }}
                  >
                    Job Ticket
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                      display: { xs: "none", sm: "table-cell" },
                    }}
                  >
                    Created
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                    }}
                  >
                    Company
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                    }}
                  >
                    Name
                  </TableCell>
                  {isInquiryPage && (
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        border: "1px solid rgba(224, 224, 224, 1)",
                      }}
                    >
                      Salesperson
                    </TableCell>
                  )}
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                    }}
                  >
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedJobs && paginatedJobs.length > 0 ? (
                  paginatedJobs.map((job, index) => {
                    // Build the navigation URL
                    const jobUrl = buildUrl(job);

                    return (
                      <TableRow
                        key={job.JobTicketID || index}
                        component={NavLink}
                        to={jobUrl}
                        sx={{
                          cursor: "pointer",
                          backgroundColor:
                            index % 2 === 0
                              ? "transparent"
                              : "rgba(0, 0, 0, 0.05)",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.08)",
                          },
                          textDecoration: "none",
                          color: "inherit",
                        }}
                      >
                        <TableCell
                          sx={{
                            border: "1px solid rgba(224, 224, 224, 1)",
                          }}
                        >
                          {job.TDControlNumber}
                        </TableCell>
                        <TableCell
                          sx={{
                            border: "1px solid rgba(224, 224, 224, 1)",
                            display: { xs: "none", sm: "table-cell" },
                          }}
                        >
                          {job.JobCreateDate}
                        </TableCell>
                        <TableCell
                          sx={{
                            border: "1px solid rgba(224, 224, 224, 1)",
                          }}
                        >
                          {job.BillingCompanyName}
                        </TableCell>
                        <TableCell
                          sx={{
                            border: "1px solid rgba(224, 224, 224, 1)",
                          }}
                        >
                          {job.JobName || job.SerialNumber || ""}
                        </TableCell>
                        {isInquiryPage && (
                          <TableCell
                            sx={{
                              border: "1px solid rgba(224, 224, 224, 1)",
                            }}
                          >
                            {(() => {
                              const emp = personnel.find(
                                (e) =>
                                  String(e.EmployeeID) ===
                                  String(job.EmployeeID),
                              );
                              return emp
                                ? `${emp.FirstName} ${emp.LastName}`
                                : "";
                            })()}
                          </TableCell>
                        )}
                        <TableCell
                          sx={{
                            border: "1px solid rgba(224, 224, 224, 1)",
                            color: (() => {
                              const status = (job.Status || "").toLowerCase();
                              if (status === "deleted") return "error.main";
                              if (status === "cancelled") return "warning.main";
                              return "success.main";
                            })(),
                          }}
                        >
                          {(() => {
                            const status = (job.Status || "").toLowerCase();
                            if (status === "deleted") return "Deleted";
                            if (status === "cancelled") return "Cancelled";
                            return job.Status || "Active";
                          })()}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={isInquiryPage ? 6 : 5}
                      align="center"
                      sx={{
                        border: "1px solid rgba(224, 224, 224, 1)",
                        py: 3,
                      }}
                    >
                      {isLoading
                        ? "Loading…"
                        : `No ${entityLabelPlural.toLowerCase()} found`}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalJobPages > 1 && (
            <Box
              sx={{
                mt: 2,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Pagination
                count={totalJobPages}
                page={jobPage}
                onChange={(event, value) => setJobPage(value)}
                color="primary"
                size={isPhone ? "small" : "medium"}
                siblingCount={isPhone ? 0 : 1}
                showFirstButton={!isPhone}
                showLastButton={!isPhone}
              />
            </Box>
          )}
        </Paper>
      </>
    );
  }

  /* Quote Page */
  if (SearchName === "Quote") {
    return (
      <>
        {/* ── Search Controls ───────────────────────────── */}
        <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 3 }, mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Search using Quote ID, Company Name, or Job Name then press Enter or
            click Search.
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ alignItems: { xs: "stretch", sm: "center" } }}
          >
            <TextField
              fullWidth
              label="Search Quotes"
              value={searchTerm}
              onChange={handleInputChange}
              onKeyDown={handleInputKey}
              variant="outlined"
            />
            <Stack direction="row" spacing={2} sx={{ flexShrink: 0 }}>
              <Button
                variant="contained"
                onClick={handleSearchNow}
                sx={{
                  minWidth: 120,
                  whiteSpace: "nowrap",
                  flex: { xs: 1, sm: "none" },
                }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={handleNewQuoteClick}
                sx={{
                  minWidth: 140,
                  whiteSpace: "nowrap",
                  flex: { xs: 1, sm: "none" },
                }}
              >
                Create Quote
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* ── Results Table ───────────────────────────── */}
        <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 3 } }}>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "center" },
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                gap: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Showing{" "}
                {paginatedQuotes.length > 0
                  ? (quotePage - 1) * quoteRowsPerPage + 1
                  : 0}
                -{Math.min(quotePage * quoteRowsPerPage, filteredQuotes.length)}{" "}
                of {filteredQuotes.length} quotes
              </Typography>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Per page</InputLabel>
                <Select
                  value={quoteRowsPerPage}
                  label="Per page"
                  onChange={(e) => {
                    setQuoteRowsPerPage(Number(e.target.value));
                    setQuotePage(1);
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Salesperson</InputLabel>
                <Select
                  value={quoteEmployeeFilter}
                  label="Salesperson"
                  onChange={(e) => {
                    setQuoteEmployeeFilter(e.target.value);
                    setQuotePage(1);
                  }}
                >
                  <MenuItem value="">All Salespeople</MenuItem>
                  {QUOTE_SALESPEOPLE.map((emp) => (
                    <MenuItem key={emp.EmployeeID} value={emp.EmployeeID}>
                      {emp.EmployeeName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {totalQuotePages > 1 && (
              <Stack spacing={2}>
                <Pagination
                  count={totalQuotePages}
                  page={quotePage}
                  onChange={(event, value) => setQuotePage(value)}
                  color="primary"
                  size={isPhone ? "small" : "medium"}
                  siblingCount={isPhone ? 0 : 1}
                  showFirstButton={!isPhone}
                  showLastButton={!isPhone}
                />
              </Stack>
            )}
          </Box>

          <SearchStatus
            isLoading={isLoading}
            error={error}
            isTruncated={isTruncated}
            truncationLimit={Limit}
          />

          <TableContainer>
            <Table
              size="small"
              sx={{ border: "1px solid rgba(224, 224, 224, 1)" }}
            >
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                    }}
                  >
                    Quote ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                      display: { xs: "none", sm: "table-cell" },
                    }}
                  >
                    Quote Date
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                    }}
                  >
                    Company
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                    }}
                  >
                    Job Name
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                    }}
                  >
                    Quote
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                      display: { xs: "none", sm: "table-cell" },
                    }}
                  >
                    Salesperson
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedQuotes && paginatedQuotes.length > 0 ? (
                  paginatedQuotes.map((quote, index) => {
                    return (
                      <TableRow
                        key={quote.BidID || index}
                        onClick={() => handleRowClick(quote)}
                        sx={{
                          cursor: "pointer",
                          backgroundColor:
                            index % 2 === 0
                              ? "transparent"
                              : "rgba(0, 0, 0, 0.05)",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.08)",
                          },
                          textDecoration: "none",
                          color: "inherit",
                        }}
                      >
                        <TableCell
                          sx={{
                            border: "1px solid rgba(224, 224, 224, 1)",
                          }}
                        >
                          {quote.BidID}
                        </TableCell>
                        <TableCell
                          sx={{
                            border: "1px solid rgba(224, 224, 224, 1)",
                            display: { xs: "none", sm: "table-cell" },
                          }}
                        >
                          {quote.BidDate}
                        </TableCell>
                        <TableCell
                          sx={{
                            border: "1px solid rgba(224, 224, 224, 1)",
                          }}
                        >
                          {quote.BidCustomerName}
                        </TableCell>
                        <TableCell
                          sx={{
                            border: "1px solid rgba(224, 224, 224, 1)",
                          }}
                        >
                          {quote.JobName}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            border: "1px solid rgba(224, 224, 224, 1)",
                          }}
                        >
                          ${quote.Bid}
                        </TableCell>
                        <TableCell
                          sx={{
                            border: "1px solid rgba(224, 224, 224, 1)",
                            display: { xs: "none", sm: "table-cell" },
                          }}
                        >
                          {(() => {
                            const emp = personnel.find(
                              (e) =>
                                String(e.EmployeeID) ===
                                String(quote.SalesmanID),
                            );
                            return emp
                              ? `${emp.FirstName} ${emp.LastName}`
                              : "";
                          })()}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      align="center"
                      sx={{
                        border: "1px solid rgba(224, 224, 224, 1)",
                        py: 3,
                      }}
                    >
                      {isLoading ? "Loading…" : "No quotes found"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalQuotePages > 1 && (
            <Box
              sx={{
                mt: 2,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Pagination
                count={totalQuotePages}
                page={quotePage}
                onChange={(event, value) => setQuotePage(value)}
                color="primary"
                size={isPhone ? "small" : "medium"}
                siblingCount={isPhone ? 0 : 1}
                showFirstButton={!isPhone}
                showLastButton={!isPhone}
              />
            </Box>
          )}
        </Paper>
      </>
    );
  }

  /* Generic / Jobs / Vendor pages */
  return (
    <Container disableGutters>
      {!Vendor && (
        <Instructions>
          Search using {SearchName}, Company Name, or {ButtonName} Name then
          press Enter or click Search.
        </Instructions>
      )}

      <SearchControls
        label={
          Vendor
            ? `Search Third Parties`
            : `Search ${ButtonName.endsWith("y") ? ButtonName.slice(0, -1) + "ies" : ButtonName + "s"}`
        }
        value={searchTerm}
        onChange={handleInputChange}
        onKeyDown={handleInputKey}
        widthProp={9}
      >
        <Button variant="contained" onClick={handleSearchNow}>
          Search
        </Button>
        {Vendor ? (
          <Button variant="outlined" color="black" onClick={createThirdParty}>
            Create {ButtonName}
          </Button>
        ) : (
          <Button
            component={RouterLink}
            to="/CompanySearch"
            variant="outlined"
            color="black"
          >
            Create {ButtonName}
          </Button>
        )}
      </SearchControls>

      {/* results */}
      <Box sx={{ mt: 2 }}>
        {(hasFetched || isLoading) && (
          <ResultsTable
            rows={notes}
            columns={columns}
            onRowClick={handleRowClick}
            stickyHeader
            isLoading={isLoading}
            error={error}
            isTruncated={isTruncated}
            truncationLimit={Limit}
            hasFetched={hasFetched}
          />
        )}
      </Box>
    </Container>
  );
}
